"use client";
import { useState } from "react";
import { SimpleTable } from "components";
import { Text, HStack, Button, Input, Select } from "@chakra-ui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useGetAllTransactionsNew } from "queries"; // Import the query

const NewTransactionsPage = () => {
  const [page, setPage] = useState(1); // Current page
  const [pageSize] = useState(10); // Page size
  const [searchType, setSearchType] = useState("email"); // Option to search by email or buyer_email
  const [query, setQuery] = useState(""); // Search query (email or buyer_email)
  const [endDate, setEndDate] = useState(new Date()); // End date filter
  const [startDate, setStartDate] = useState(() => {
    const newDate = new Date();
    newDate.setDate(newDate.getDate() - 1); // Default to 1 day before endDate
    return newDate;
  });

  // Fetch transactions using the new query hook
  const { data, isLoading } = useGetAllTransactionsNew({
    page,
    page_size: pageSize,
    query: query || undefined, // Pass query only if present
    searchType, // Pass the search type (email or buyer_email)
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
  });

  // Reset page when query or searchType changes
  const handleQueryChange = (e: any) => {
    setQuery(e.target.value);
    setPage(1); // Reset to the first page
  };

  // Table columns for rendering credit transactions
  const creditTransactionColumns = [
    {
      title: "Email",
      align: "start",
      format: (transaction: any) => (
        <Text variant="unstyled" minWidth="150px">
          {transaction.email ? transaction.email : "User does not exist"} {/* Display email or "User does not exist" */}
        </Text>
      ),
    },
    {
      title: "Buyer Email",
      align: "start",
      format: (transaction: any) => (
        <Text variant="unstyled" minWidth="150px">
          {transaction.buyer_email ? transaction.buyer_email : ""} {/* Display buyer email or leave blank */}
        </Text>
      ),
    },
    {
      title: "Credit Amount",
      align: "start",
      format: (transaction: any) => (
        <Text textAlign="center" minWidth="100px">
          {transaction.credit_amount}
        </Text>
      ), // Display credit amount
    },
    {
      title: "Transaction ID",
      align: "start",
      format: (transaction: any) => (
        <Text variant="unstyled" minWidth="150px">
          {transaction.payment_intent_id          }
        </Text>
      ),
    },
    {
      title: "Source",
      align: "start",
      format: (transaction: any) => {
        let displaySource;
        switch (transaction.source) {
          case "one_time_top_up":
            displaySource = "PayPal";
            break;
          case "crypto_top_up":
            displaySource = "Crypto";
            break;
          case "stripe_top_up":
            displaySource = "Stripe";
            break;
          case "payabl_top_up":
            displaySource = "Payabl";
            break;
          default:
            displaySource = transaction.source;
        }
        return <Text>{displaySource}</Text>;
      },
    },
    {
      title: "Created At",
      align: "start",
      format: (transaction: any) => (
        <Text>
          {new Date(transaction.created_at).toLocaleString("en-US", {
            timeZone: "UTC",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </Text> // Display UTC format
      ),
    },
  ];

  return (
    <div>
      <HStack spacing="16px" align="center" wrap="wrap">
        <Select
          value={searchType}
          onChange={(e) => setSearchType(e.target.value)} // Select search type: email or buyer_email
          width="200px" // Adjust the width of the dropdown
        >
          <option value="email">Search by Email</option>
          <option value="buyer_email">Search by Buyer Email</option>
        </Select>

        <Input
          placeholder={`Search by ${searchType}`} // Dynamic placeholder based on search type
          value={query}
          onChange={handleQueryChange} // Update the query (email or buyer_email)
          flex="1" // Make the input field flexible
        />

        <Text>From:</Text>
        <DatePicker
          customInput={<Input />}
          selected={startDate}
          onChange={(date) => setStartDate(date!)}
        />
        <Text>To:</Text>
        <DatePicker
          customInput={<Input />}
          selected={endDate}
          onChange={(date) => setEndDate(date!)}
        />
      </HStack>

      <HStack spacing="24px" mt="4">
        <Button onClick={() => setPage(1)}>First page</Button>
        <Button onClick={() => setPage((old) => Math.max(old - 1, 1))}>Previous page</Button>
        <Text>Page: {page}</Text>
        <Button onClick={() => setPage((old) => old + 1)}>Next page</Button>
      </HStack>

      <SimpleTable
        columns={creditTransactionColumns}
        data={data ? data.credit_transactions : []}
        isLoading={isLoading}
        templateColumns="repeat(6, 1fr)" // Updated to account for the new Buyer Email column
      />
    </div>
  );
};

export default NewTransactionsPage;
